import React, { FC, Fragment, useMemo, useContext, useRef, useState, useEffect } from "react";

import InfoIcon from '@pixel/assets/icons/Info'
import HelpIcon from '@pixel/assets/icons/Help'

import AutoSelect from '@pixel/core/AutoSelect';
import Checkbox from "@pixel/core/Checkbox";
import CircularProgress from "@pixel/core/CircularProgress";
import ContentModal from "@pixel/core/ContentModal";
import FileUpload from '@pixel/core/FileUpload/FileUpload';
import FormControlLabel from '@pixel/core/FormControlLabel';
import FormGroup from '@pixel/core/FormGroup';
import FormInputLayout from '@pixel/core/FormInputLayout';
import Stack from '@pixel/core/Stack';
import TextField from '@pixel/core/TextField';
import ToolTip from '@pixel/core/Tooltip';
import Typography from "@pixel/core/Typography";
import { useSnackbar } from '@pixel/core/SnackbarService';

import HTMLEditor from '@pixel/internal/Editor/HTMLEditor';

import EmployeeAutoComplete from "../EmployeeAutoComplete";
import CandidateListContext from "../../contexts/candidate/candidateListContext";
import { useLocaleType, useMultipleLocalizedText } from "../../contexts/Localization/localizationHooks";
import { useGetCandidateNoteTemplates, CandidateNoteTemplateWithLocale, useSendCandidateNoteRequest } from "../../hooks/useSendCandidateNote";
import { useJHireURL } from "../../contexts/Domain";
import formatPluralString from '../../utils/formatPluralString'
import convertLineBreaksToBR, { removeLineBreaks } from "../../utils/formatEmailBody";
import FloatingCircularProgress from "./FloatingCircularProgress";

interface ICallback {
  (): void;
}

interface ActionModalProps {
  open: boolean;
  onClose: ICallback;
}

const accept = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/pdf': ['.pdf'],
  'application/dmg': ['.dmg'],
  'application/doc': ['.doc'],
}

const SendNotesModal: FC<ActionModalProps> = ({
  open = false,
  onClose = () => { },
}) => {
  const jhireURL = useJHireURL()
  const userLocalePreference = useLocaleType()
  const {
    COMMON_SEND_NOTES,
    COMMON_CANDIDATE,
    COMMON_CANDIDATES,
    COMMON_SUBJECT,
    COMMON_TEMPLATE,
    CANCEL,
    COMMON_SUBJECT_IS_REQUIRED,
    JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD,
    COMMON_FILE_UPLOAD,
    COMMON_FILE_DROP,
    JV_APPLICANT_NOTE_ADDED_SUCCESSFULLY,
    JV_SENDNOTE_CONTROLLER_MEESAGE_SENT_ERROR,
  } = useMultipleLocalizedText()

  const snack = useSnackbar()
  const {
    validatedCandidates,
    validatedCandidateIds,
  } = useValidatedCandidatesList()

  const getCandidateNoteProps = useMemo(() => ({
    applications: validatedCandidateIds,
  }), [validatedCandidateIds])

  const {
    loading: templatesLoading,
    error: templatesError,
    templates,
  } = useGetCandidateNoteTemplates(getCandidateNoteProps)

  const [employees, setEmployees] = useState([])
  const [templateChosen, setTemplateChosen] = useState<CandidateNoteTemplateWithLocale>(null)
  const templateRef = useRef(null)
  templateRef.current = templateChosen
  const [subject, setSubject] = useState('')
  const bodyEditorRef = useRef(null)
  const [includeResume, setIncludeResume] = useState(true)
  const [files, setFiles] = useState([])

  const [subjectError, setSubjectError] = useState('')
  const [contentError, setContentError] = useState('')

  const {
    loading: sendLoading,
    error: sendError,
    response: sendResponse,
    sendNoteRequest,
  } = useSendCandidateNoteRequest()

  const handleReject = () => {
    console.log("Cancel Modal");
  };
  const handleResolve = () => {
    setSubjectError('')
    setContentError('')

    let hasError = false

    if (subject === '') {
      setSubjectError(COMMON_SUBJECT_IS_REQUIRED)
      hasError = true
    }

    if (bodyEditorRef.current.getValue() === '') {
      setContentError(JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD)
      hasError = true
    }

    if (hasError) {
      return true
    }

    sendNoteRequest({
      attachmentList: files.map(file => file.response.resourceInfo.eid),
      content: removeLineBreaks(bodyEditorRef.current.getValue()),
      entityIdList: validatedCandidateIds,
      includeResume: includeResume,
      locale: (templateChosen && templateChosen.locale) ? templateChosen.locale : undefined,
      subject: subject,
      toList: employees.map(employee => employee.EId)
    })
      .then((response) => {
        snack.showSuccess(JV_APPLICANT_NOTE_ADDED_SUCCESSFULLY)
        onClose()
      })
      .catch((err) => {
        snack.showError(JV_SENDNOTE_CONTROLLER_MEESAGE_SENT_ERROR)
      })

    return true
  };

  const handleHTMLEditorLoad = () => {
    if (bodyEditorRef.current && templateRef.current) {
      bodyEditorRef.current.setValue(convertLineBreaksToBR(templateRef.current.body))
    }
  }

  const validNamesList = useMemo(() => {
    if (validatedCandidates.length > 0) {
      return validatedCandidates.map(candidate => <Fragment key={candidate.EId}>{candidate.candidate.fullName} <br /></Fragment>)
    }

    return ''
  }, [validatedCandidates])

  useEffect(() => {
    const userLocaleTemplates = templates?.filter(template => template.locale.toLowerCase() === userLocalePreference.toLowerCase()) || []
    const defaultTemplate = userLocaleTemplates.find(template => template.isDefault)
    if (defaultTemplate) {
      setTemplateChosen(defaultTemplate)
    } else if (templates.length > 0) {
      setTemplateChosen(templates[0])
    } else {
      setTemplateChosen(null)
    }
  }, [templates])

  useEffect(() => {
    if (templateChosen) {
      setSubject(templateChosen.subject)
      if (bodyEditorRef.current) {
        bodyEditorRef.current.setValue(convertLineBreaksToBR(templateChosen.body))
      }
    }
  }, [templateChosen])

  return (
    <ContentModal
      id="send-notes-candidate"
      open={open}
      onClose={onClose}
      showClose={true}
      sizeVariant="md"
      title={COMMON_SEND_NOTES}
      labelCloseAria="close"
      rejectActions={[{ reason: "cancel", label: CANCEL }]}
      resolveActions={[{ reason: "ok", label: COMMON_SEND_NOTES }]}
      onResolve={handleResolve}
      onReject={handleReject}
      modalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
      }}
    >
      <Stack>
        {templatesLoading && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        )}
        {
          !templatesLoading && (
            <>
              <FloatingCircularProgress open={sendLoading} />
              <ToolTip title={<>{validNamesList}</>} placement="bottom-start">
                <div>
                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                    {validNamesList.length} {' '}
                    {formatPluralString(
                      validNamesList.length,
                      COMMON_CANDIDATE,
                      COMMON_CANDIDATES,
                    )}
                    <InfoIcon sx={{ marginLeft: '5px' }} />
                  </Typography>
                </div>
              </ToolTip>
              <FormInputLayout gridLayout="calc(100%)">
                <EmployeeAutoComplete
                  optional
                  value={employees}
                  onChange={(event, newEmployees: any) => {
                    setEmployees(newEmployees)
                  }}
                />
              </FormInputLayout>
              <FormInputLayout gridLayout="calc(100%)">
                <AutoSelect
                  label={COMMON_TEMPLATE}
                  value={templateChosen}
                  options={templates}
                  groupBy={(option: CandidateNoteTemplateWithLocale) => option.group}
                  onChange={(event: any, value: CandidateNoteTemplateWithLocale) => setTemplateChosen(value)}
                  getOptionLabel={(option: CandidateNoteTemplateWithLocale) => option.title}
                  isOptionEqualToValue={(option: CandidateNoteTemplateWithLocale, value: CandidateNoteTemplateWithLocale) => option.EId === value.EId}
                />
              </FormInputLayout>
              <FormInputLayout gridLayout="calc(100%)">
                <TextField
                  required
                  label={COMMON_SUBJECT}
                  fullWidth
                  margin="normal"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  errorText={subjectError}
                  error={subjectError !== ''}
                />
              </FormInputLayout>
              <HTMLEditor
                ref={bodyEditorRef}
                variant="standard"
                enterMode="br"
                disabled={false}
                mergeFields={{}}
                initialValue={convertLineBreaksToBR(((templateChosen || {}).body) || '')}
                onLoad={handleHTMLEditorLoad}
              />
              {contentError && <Typography sx={{ color: 'red' }}>{JV_SENDMESSAGE_CONTROLLER_THIS_IS_A_REQUIRED_FIELD}</Typography>}
              <div style={{ marginTop: '24px' }} />
              <FormGroup row>
                <FormControlLabel control={<Checkbox checked={includeResume} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setIncludeResume(event.target.checked)} />} label="Include Candidate Resumes if available" />
              </FormGroup>
              <FileUpload
                btnMultiLabel={COMMON_FILE_UPLOAD}
                dragMultiLabel={COMMON_FILE_DROP}
                maxFiles={4}
                accept={accept}
                multiple
                uploadUrl={`${jhireURL}/action/resource/attachment?&getDownloadToken=true`}
                onChange={(files) => setFiles(files)}
              />
            </>
          )
        }
      </Stack>
    </ContentModal>
  );
};

export default SendNotesModal;

function useValidatedCandidatesList() {
  const context = useContext(CandidateListContext);

  if (context === null) {
    throw 'This hook must be used within a <CandidateActionModal /> child'
  }

  return useMemo(() => {
    if (context.validatedCandidateList && context.validatedCandidateList.validApplications) {
      return {
        validatedCandidates: context.validatedCandidateList.validApplications,
        validatedCandidateIds: context.validatedCandidateList.validApplicationIds,
      }
    }

    return {
      validatedCandidates: [],
      validatedCandidateIds: [],
    }
  }, [
    context,
    context.validatedCandidateList,
    context.validatedCandidateList.validApplication,
  ])
}
